<template>
  <div>
    <ValidationProvider
      v-slot="{ errors }"
      :vid="vid"
      :name="name"
      :rules="rules"
      tag="div"
      ref="provider"
    >
      <AGCLabel v-if="label" :required="required">
        <slot>{{ label }}</slot>
        <template v-slot:subLabel>
          <slot name="subLabel">{{ subLabel }}</slot>
        </template>
      </AGCLabel>
      <div v-if="readOnly" v-html="inputVal" class="content-editor--readonly"></div>
      <!-- v-show for Editor work only if applied on a wrapping div -->
      <div v-show="!readOnly">
        <Editor
          v-model="inputVal"
          :apiKey="apiKey"
          :init="options"
          :disabled="isEditorLoading"
          @onInit="isEditorLoading = false"
          @onBeforeSetContent="handlePasteImg"
        />
      </div>
      <span v-if="errors" class="content-editor__error">{{ errors[0] }}</span>
      <span style="float: right; color: rgba(34,47,62,.7);">
        字數：{{ inputVal | wordCount }}
      </span>
    </ValidationProvider>

    <!-- modals for custom plugins: -->
    <AddPromotionDateDialog
      id="promotiondate-dialog"
      :htmlCallback="
        html => {
          this.editor.execCommand('mceInsertContent', false, html);
        }
      "
    />
    <AddYoutubeDialog
      id="youtube-dialog"
      :htmlCallback="
        html => {
          this.editor.execCommand('mceInsertContent', false, html);
        }
      "
    />
    <AddBlogContentImageDialog
      id="contentimage-dialog"
      :htmlCallback="
        html => {
          this.editor.execCommand('mceInsertContent', false, html);
        }
      "
    />
    <AddCTAButtonDialog
      id="ctabutton-dialog"
      :htmlCallback="
        html => {
          this.editor.execCommand('mceInsertContent', false, html);
        }
      "
    />
  </div>
</template>

<script>
import AddCTAButtonDialog from '@/components/editor/modals/AddCTAButtonDialog.vue';
import AddBlogContentImageDialog from '@/components/editor/modals/AddBlogContentImageDialog.vue';
import AddPromotionDateDialog from '@/components/editor/modals/AddPromotionDateDialog.vue';
import AddYoutubeDialog from '@/components/editor/modals/AddYoutubeDialog.vue';
import AGCLabel from '@/components/form/AGCLabel.vue';
import imagePath from '@/filters/image-path';
import vModelMixin from '@/mixins/v-model';
import Editor from '@tinymce/tinymce-vue';
import axios from 'axios';
import { ValidationProvider } from 'vee-validate';
import wordCount from '@/filters/word-count';
import { mapGetters } from 'vuex';

export default {
  mixins: [vModelMixin],
  components: {
    AddCTAButtonDialog,
    AddBlogContentImageDialog,
    AddPromotionDateDialog,
    AddYoutubeDialog,
    AGCLabel,
    Editor,
    ValidationProvider,
  },
  props: {
    vid: {
      type: String,
      default: undefined,
    },
    rules: [Object, String],
    name: String,
    label: String,
    subLabel: String,
    uploadType: String,
    height: {
      type: Number,
      default: 500,
    },
    readOnly: Boolean,
    required: Boolean,
    menubar: {
      type: String,
      default: '',
    },
    toolbar: {
      type: [String, Array],
      default: () => [`
        undo redo |
        bold italic underline strikethrough |
        alignleft aligncenter alignright alignjustify |
        bullist numlist outdent indent |
        superscript subscript |
        image table link blockquote hr |
        charmap
      `],
    },
    block_formats: {
      type: String,
      default: 'Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3',
    },
    pasteImg: {
      type: Boolean,
      default: false,
    },
    init: Object,
  },
  data() {
    return {
      editor: null,
      isEditorLoading: true,
      apiKey: 'no-api-key',
    };
  },
  computed: {
    ...mapGetters(['role']),
    options() {
      return {
        content_css: `${process.env.VUE_APP_TINYMCE_CSS_PATH}?${new Date().getTime()}`,
        language_url: process.env.VUE_APP_TINYMCE_LANGUAGE_PATH,
        language: 'zh_TW',
        height: this.height,
        plugins: [
          // 'autoresize',
          'quickbars',
          'paste',
          'hr',
          'lists advlist',
          'image imagetools',
          'template',
          'autolink link charmap',
          'textpattern',
          'searchreplace visualblocks fullscreen',
          'preview anchor insertdatetime',
          'importcss code help table',
        ],
        menubar: this.menubar,
        toolbar: this.toolbar,
        block_formats: this.block_formats,
        convert_urls: false,
        image_title: true,
        image_caption: true,
        image_dimensions: false,
        images_upload_handler: this.uploadType ? this.handleImageUpload : null,
        quickbars_image_toolbar: 'alignleft aligncenter alignright',
        quickbars_selection_toolbar: 'bold italic underline | quicklink',
        insertdatetime_formats: ['%Y年%m月%d日', '%I:%M:%S %p'],
        imagetools_toolbar: 'imageoptions',
        quickbars_insert_toolbar: false,
        nowrap: false,
        ...this.init,
        setup: (editor) => {
          this.editor = editor;

          editor.ui.registry.addButton('promotiondate', {
            text: '<div style="cursor: pointer">推廣日期</div>',
            onAction: () => {
              editor.execCommand('ToggleToolbarDrawer');
              this.$bvModal.show('promotiondate-dialog');
            },
          });
          editor.ui.registry.addButton('youtube', {
            text: '<div style="cursor: pointer">YouTube 影片</div>',
            onAction: () => {
              editor.execCommand('ToggleToolbarDrawer');
              this.$bvModal.show('youtube-dialog');
            },
          });
          editor.ui.registry.addButton('blogcontentimage', {
            text: '<div style="cursor: pointer">圖片</div>',
            onAction: () => {
              editor.execCommand('ToggleToolbarDrawer');
              this.$bvModal.show('contentimage-dialog');
            },
          });
          editor.ui.registry.addButton('ctabutton', {
            text: '<div style="cursor: pointer">行動呼籲按鈕</div>',
            onAction: () => {
              editor.execCommand('ToggleToolbarDrawer');
              this.$bvModal.show('ctabutton-dialog');
            },
          });

          editor.ui.registry.addContextToolbar('ctabuttonselection', {
            predicate: (node) => node.nodeName === 'SPAN' && node.className === 'ctabutton',
            items: 'forecolor backcolor',
            position: 'node',
          });
        },
      };
    },
  },
  filters: {
    imagePath,
    wordCount,
  },
  methods: {
    handleImageUpload({ blob }, success, failure) {
      const data = new FormData();
      data.append('upload', blob());
      axios({
        method: 'POST',
        url: `/agc-${this.role}-api/upload/image`,
        params: { type: this.uploadType },
        data,
      })
        .then(({ data: { payload } }) => {
          success(this.$options.filters.imagePath(payload, 'couponContentImages', 'default', false));
        })
        .catch((e) => {
          failure(e);
        });
    },
    handlePasteImg(e) {
      if (!this.pasteImg) {
        if (e.content.includes('<img src')) {
          e.preventDefault();
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.content-editor__error {
  font-size: 80%;
  color: #dc3545;
}

.content-editor--readonly {
  word-wrap: break-word;
}

::v-deep {
  .tox-tinymce:disabled {
    cursor: wait;
  }
}
</style>
